import React, { useState, useMemo } from "react";
import { Autocomplete, Select, Table, Button, Tag } from "@ann/web-components";
import Footer from "../../../../../../components/Footer/Footer";
import {
  ExecutionLogContainer,
  FilterContainer,
  TableContainer,
} from "./Execution.styled";
import { useExecution } from "../../../../../../context/ExecutionContext";

const Execution = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { filters, updateFilters, clearFilters } = useExecution();
  const [autocompleteValue, setAutocompleteValue] = useState("");

  const emptyOrValue = (val) => (val ? val : undefined);

  const handleAutocompleteChange = (value) => setAutocompleteValue(value);
  const handleAutocompleteClear = () => setAutocompleteValue("");
  const handleClearAll = () => {
    setAutocompleteValue("");
    clearFilters();
  };

  const getStatusIcon = (status) => "info";

  const getStatusColor = (status) => {
    switch (status) {
      case "received":
      case "in progress":
        return "informative";
      case "done":
        return "success";
      case "failed":
        return "error";
      case "waiting":
      case "blocked":
        return "alert";
      default:
        return "neutral";
    }
  };

  const capitalize = (text) =>
    text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

  const rawData = [
    {
      uuid: "1",
      eventId: "EVT-001",
      eventName: "Email Welcome Trigger",
      node: "node1",
      receivedDate: "2024-01-15 10:30:00",
      lastUpdate: "2024-01-15 10:35:00",
      statusRaw: "received",
    },
    {
      uuid: "2",
      eventId: "EVT-002",
      eventName: "SMS Reminder Trigger",
      node: "node2",
      receivedDate: "2024-01-14 15:45:00",
      lastUpdate: "2024-01-14 15:50:00",
      statusRaw: "waiting",
    },
    {
      uuid: "3",
      eventId: "EVT-003",
      eventName: "Push Notification Trigger",
      node: "node3",
      receivedDate: "2024-01-13 09:20:00",
      lastUpdate: "2024-01-13 09:25:00",
      statusRaw: "in progress",
    },
    {
      uuid: "4",
      eventId: "EVT-004",
      eventName: "Webhook Trigger",
      node: "node1",
      receivedDate: "2024-01-12 14:15:00",
      lastUpdate: "2024-01-12 14:20:00",
      statusRaw: "done",
    },
    {
      uuid: "5",
      eventId: "EVT-005",
      eventName: "In-App Notification Trigger",
      node: "node2",
      receivedDate: "2024-01-11 11:30:00",
      lastUpdate: "2024-01-11 11:35:00",
      statusRaw: "failed",
    },
    {
      uuid: "6",
      eventId: "EVT-006",
      eventName: "Error Handling Trigger",
      node: "node3",
      receivedDate: "2024-01-10 08:15:00",
      lastUpdate: "2024-01-10 08:20:00",
      statusRaw: "blocked",
    },
  ];

  const allTableData = rawData.map((item) => ({
    ...item,
    status: (
      <Tag
        text={capitalize(item.statusRaw)}
        color={getStatusColor(item.statusRaw)}
        icon={getStatusIcon(item.statusRaw)}
      />
    ),
  }));

  const filteredTableData = useMemo(() => {
    return allTableData.filter((item) => {
      if (
        filters.status &&
        filters.status !== "all" &&
        item.statusRaw !== filters.status
      )
        return false;
      if (filters.node && item.node !== filters.node) return false;
      if (filters.lastUpdate) {
        return true;
      }
      return true;
    });
  }, [allTableData, filters]);

  const headers = [
    { value: "eventId", label: "Event ID" },
    { value: "eventName", label: "Event Name" },
    { value: "node", label: "Node" },
    { value: "receivedDate", label: "Received Date" },
    { value: "lastUpdate", label: "Last Update" },
    { value: "status", label: "Status" },
  ];

  return (
    <>
      <ExecutionLogContainer>
        <div className="title">Execution Log</div>
        <div className="description">
          Monitor, troubleshoot, and understand how your workflows perform in
          real time.
        </div>
      </ExecutionLogContainer>

      <FilterContainer>
        <Autocomplete
          value={autocompleteValue}
          onChange={handleAutocompleteChange}
          onSearch={() => {}}
          onSuggestionSelect={() => {}}
          onClear={handleAutocompleteClear}
          placeholder="Search workflows..."
          suggestions={["Prompt 1", "Prompt 2", "Prompt 3"]}
          disabled={true}
        />
        <Select
          label="Status"
          placeholder="Status"
          onChange={(value) => updateFilters({ status: value })}
          size="small"
          value={emptyOrValue(filters.status)}
          width="auto"
          minWidth="139px"
          disabled={true}
        >
          <Select.Menu>
            <Select.Item value="all" label="All">
              All
            </Select.Item>
            <Select.Item value="received" label="Received">
              Received
            </Select.Item>
            <Select.Item value="waiting" label="Waiting">
              Waiting
            </Select.Item>
            <Select.Item value="in progress" label="In Progress">
              In Progress
            </Select.Item>
            <Select.Item value="done" label="Done">
              Done
            </Select.Item>
            <Select.Item value="failed" label="Failed">
              Failed
            </Select.Item>
            <Select.Item value="blocked" label="Blocked">
              Blocked
            </Select.Item>
          </Select.Menu>
        </Select>

        <Select
          label="Execution Node"
          placeholder="Execution Node"
          onChange={(value) => updateFilters({ node: value })}
          size="small"
          value={emptyOrValue(filters.node)}
          width="auto"
          minWidth="139px"
          disabled={true}
        >
          <Select.Menu>
            <Select.Item value="node1" label="Node 1">
              Node 1
            </Select.Item>
            <Select.Item value="node2" label="Node 2">
              Node 2
            </Select.Item>
            <Select.Item value="node3" label="Node 3">
              Node 3
            </Select.Item>
          </Select.Menu>
        </Select>

        <Select
          label="Last Update"
          placeholder="Last Update"
          onChange={(value) => updateFilters({ lastUpdate: value })}
          size="small"
          value={emptyOrValue(filters.lastUpdate)}
          width="auto"
          minWidth="139px"
          disabled={true}
        >
          <Select.Menu>
            <Select.Item value="today" label="Today">
              Today
            </Select.Item>
            <Select.Item value="yesterday" label="Yesterday">
              Yesterday
            </Select.Item>
            <Select.Item value="lastWeek" label="Last Week">
              Last Week
            </Select.Item>
            <Select.Item value="lastMonth" label="Last Month">
              Last Month
            </Select.Item>
          </Select.Menu>
        </Select>

        <Button
          label="Clear Filters"
          size="medium"
          type="secondary"
          onClick={handleClearAll}
          disabled={true}
        />
      </FilterContainer>

      <TableContainer>
        <Table
          headers={headers}
          data={filteredTableData}
          action={false}
          showActions={false}
        />
      </TableContainer>

      <Footer
        currentPage={currentPage}
        totalPages={1}
        itemsPerPage={10}
        totalItems={filteredTableData.length}
        onPageChange={setCurrentPage}
        disabled={false}
      />
    </>
  );
};

export default Execution;
